import type {
  ConversationWorkflow,
  ConversationWorkflowEventsResponse,
} from "./conversationWorkflowSchemas";

export interface ConversationWorkflowScope {
  organizationId: string;
  conversationId: string;
  shopBindingId: string;
  userId: string;
}

export function conversationWorkflowScopeKey(scope: ConversationWorkflowScope) {
  return [scope.organizationId, scope.conversationId, scope.shopBindingId, scope.userId].join("\u0000");
}

export function workflowBelongsToScope(
  workflow: ConversationWorkflow,
  scope: ConversationWorkflowScope,
) {
  const expectedShop = scope.shopBindingId || null;
  return workflow.organization_id === scope.organizationId &&
    workflow.conversation_id === scope.conversationId &&
    workflow.actor_user_id === scope.userId &&
    workflow.shop_binding_id === expectedShop;
}

// Snapshot 和增量事件都走同一单调合并器。较旧 revision 永远不能覆盖
// 已经看到的终态，跨会话、跨店铺或跨 actor 的数据则直接丢弃。
export function mergeConversationWorkflowProjection(
  current: ReadonlyArray<ConversationWorkflow>,
  incoming: ReadonlyArray<ConversationWorkflow>,
  scope: ConversationWorkflowScope,
) {
  const byId = new Map(
    current
      .filter((workflow) => workflowBelongsToScope(workflow, scope))
      .map((workflow) => [workflow.workflow_id, workflow] as const),
  );
  for (const workflow of incoming) {
    if (!workflowBelongsToScope(workflow, scope)) continue;
    const existing = byId.get(workflow.workflow_id);
    // 同一 revision 是不可变事实；迟到且内容冲突的 snapshot 不能覆盖
    // 已经展示的可信投影。
    if (!existing || workflow.revision > existing.revision) {
      byId.set(workflow.workflow_id, workflow);
    }
  }
  return [...byId.values()].sort((left, right) =>
    left.started_at.localeCompare(right.started_at) || left.workflow_id.localeCompare(right.workflow_id));
}

export function acceptConversationWorkflowEvents(
  current: ReadonlyArray<ConversationWorkflow>,
  response: ConversationWorkflowEventsResponse,
  scope: ConversationWorkflowScope,
  expectedWorkflowId: string,
  expectedAfterRevision: number,
) {
  if (response.workflow_id !== expectedWorkflowId || response.after_revision !== expectedAfterRevision ||
    response.current_revision < expectedAfterRevision) {
    return { accepted: false as const, currentRevision: expectedAfterRevision, workflows: [...current] };
  }
  if (response.workflow && !workflowBelongsToScope(response.workflow, scope)) {
    return { accepted: false as const, currentRevision: expectedAfterRevision, workflows: [...current] };
  }
  const knownRevision = current.find((workflow) => workflow.workflow_id === expectedWorkflowId)?.revision ?? 0;
  if (response.current_revision < knownRevision) {
    return { accepted: false as const, currentRevision: expectedAfterRevision, workflows: [...current] };
  }
  return {
    accepted: true as const,
    currentRevision: response.current_revision,
    workflows: response.workflow
      ? mergeConversationWorkflowProjection(current, [response.workflow], scope)
      : [...current],
  };
}
